/**
 * Três gráficos de barras horizontais para Unidades por Situação
 * Regular, Em Renovação, Irregular
 * Similar ao documentosUnidadesSituacaoCharts.js do backup
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import type { UnitsBySituationResponse } from '../hooks';

interface UnidadesSituacaoChartsProps {
  data: UnitsBySituationResponse | undefined;
  isLoading?: boolean;
  className?: string;
}

interface MiniBarChartProps {
  title: string;
  value: number;
  total: number;
  color: string;
  bgColor: string;
}

function MiniBarChart({ title, value, total, color, bgColor }: MiniBarChartProps) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  const option: EChartsOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'none' },
      formatter: `${title}: ${value.toLocaleString('pt-BR')} documentos (${percentage.toFixed(1)}%)`,
    },
    grid: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    xAxis: {
      type: 'value',
      max: total || 1,
      show: false,
    },
    yAxis: {
      type: 'category',
      data: [title],
      show: false,
    },
    series: [
      {
        type: 'bar',
        data: [value],
        barWidth: '100%',
        itemStyle: {
          color,
          borderRadius: 4,
        },
        showBackground: true,
        backgroundStyle: {
          color: bgColor,
          borderRadius: 4,
        },
      },
    ],
  }), [title, value, total, color, bgColor, percentage]);

  return (
    <div className="flex-1 min-h-[70px]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium" style={{ color }}>{title}</span>
        <span className="text-sm font-bold">{value.toLocaleString('pt-BR')}</span>
      </div>
      <ReactECharts
        option={option}
        style={{ height: '30px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      <div className="text-xs text-muted-foreground text-right mt-0.5">
        {percentage.toFixed(1)}% do total
      </div>
    </div>
  );
}

export function UnidadesSituacaoCharts({
  data,
  isLoading = false,
  className,
}: UnidadesSituacaoChartsProps) {
  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <h3 className="text-sm font-medium text-muted-foreground mb-4">
          Unidades por Situação (Documentos)
        </h3>
        <div className="flex flex-col gap-4 animate-pulse">
          <div className="h-16 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <h3 className="text-sm font-medium text-muted-foreground mb-4">
          Unidades por Situação (Documentos)
        </h3>
        <div className="flex items-center justify-center h-[200px] text-muted-foreground">
          Sem dados disponíveis
        </div>
      </div>
    );
  }

  const total = data.total;

  return (
    <div className={cn('bg-card rounded-xl p-4 shadow-sm h-[380px] flex flex-col', className)}>
      <h3 className="text-sm font-medium text-muted-foreground mb-4">
        Unidades por Situação (Documentos)
      </h3>
      <div className="flex flex-col gap-4 flex-1 justify-center">
        <MiniBarChart
          title="Regular"
          value={data.regular.count}
          total={total}
          color="#10b981" // emerald-500
          bgColor="rgba(16, 185, 129, 0.15)"
        />
        <MiniBarChart
          title="Em Renovação"
          value={data.renovacao.count}
          total={total}
          color="#f59e0b" // amber-500
          bgColor="rgba(245, 158, 11, 0.15)"
        />
        <MiniBarChart
          title="Irregular"
          value={data.irregular.count}
          total={total}
          color="#ef4444" // red-500
          bgColor="rgba(239, 68, 68, 0.15)"
        />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 border-t pt-3 text-center">
        <div>
          <span className="text-sm text-muted-foreground">Total de Unidades: </span>
          <span className="text-sm font-bold">{data.totalUnits.toLocaleString('pt-BR')}</span>
        </div>
        <div>
          <span className="text-sm text-muted-foreground">Documentos avaliados: </span>
          <span className="text-sm font-bold">{total.toLocaleString('pt-BR')}</span>
        </div>
      </div>
    </div>
  );
}
